import React, { useState, useEffect, useRef } from 'react';
import { Star, MessageCircle, Trash2, Image, Send, ChevronDown } from 'lucide-react';
import { reviewsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const StarRating = ({ value, onChange, size = 20 }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map(n => (
      <button
        key={n}
        type="button"
        onClick={() => onChange && onChange(n)}
        className="transition-transform hover:scale-110"
      >
        <Star
          size={size}
          className={n <= value ? 'text-yellow-400' : 'text-gray-600'}
          fill={n <= value ? 'currentColor' : 'none'}
        />
      </button>
    ))}
  </div>
);

const AvgStars = ({ avg, total }) => (
  <div className="flex items-center gap-2">
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} size={14} className={n <= Math.round(avg) ? 'text-yellow-400' : 'text-gray-600'} fill={n <= Math.round(avg) ? 'currentColor' : 'none'} />
      ))}
    </div>
    <span className="text-white font-bold text-sm">{avg > 0 ? avg.toFixed(1) : '—'}</span>
    <span className="text-gray-500 text-xs">({total} {total === 1 ? 'review' : 'reviews'})</span>
  </div>
);

function ReplyBox({ reviewId, onReplied }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const submit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const r = await reviewsAPI.reply(reviewId, text.trim());
      onReplied(r.data);
      setText('');
    } catch {
      toast.error('Failed to post reply');
    } finally { setLoading(false); }
  };

  return (
    <div className="flex gap-2 mt-3">
      <input
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submit()}
        placeholder="Write a reply..."
        className="flex-1 bg-[#111] border border-gray-700 text-white text-xs px-3 py-2 rounded focus:border-red-500 outline-none"
        data-testid={`reply-input-${reviewId}`}
      />
      <button onClick={submit} disabled={loading || !text.trim()} className="px-3 py-2 btn-primary rounded text-xs font-bold disabled:opacity-40">
        <Send size={12} />
      </button>
    </div>
  );
}

function ReviewCard({ review, currentUserId, onDeleted }) {
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState(review.replies || []);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const { t } = useLanguage();
  const { user } = useAuth();

  const handleDelete = async () => {
    try {
      await reviewsAPI.delete(review.review_id);
      onDeleted(review.review_id);
      toast.success('Review deleted');
    } catch { toast.error('Could not delete'); }
  };

  const handleReplied = (reply) => {
    setReplies(r => [...r, reply]);
    setShowReplies(true);
    setShowReplyBox(false);
  };

  return (
    <div className="bg-[#0D0D0D] border border-gray-800/80 rounded-xl p-5" data-testid={`review-${review.review_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-600/30 to-gray-800 flex items-center justify-center text-sm font-bold text-white flex-shrink-0 overflow-hidden">
            {review.user_picture
              ? <img src={review.user_picture} alt="" className="w-full h-full object-cover" />
              : (review.user_name || 'A')[0].toUpperCase()
            }
          </div>
          <div>
            <p className="text-white font-semibold text-sm">{review.user_name}</p>
            <p className="text-gray-600 text-xs">{review.created_at?.slice(0, 10)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StarRating value={review.rating} size={14} />
          {(currentUserId === review.user_id || user?.role === 'super_admin') && (
            <button onClick={handleDelete} className="text-gray-600 hover:text-red-400 transition-colors ml-1">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      <p className="text-gray-300 text-sm leading-relaxed mb-3">{review.text}</p>

      {review.photo_base64 && (
        <div className="mb-3 rounded-lg overflow-hidden max-w-xs">
          <img src={`data:image/jpeg;base64,${review.photo_base64}`} alt="Review" className="w-full object-cover max-h-48" />
        </div>
      )}

      {/* Replies */}
      {replies.length > 0 && (
        <button
          onClick={() => setShowReplies(v => !v)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-xs font-medium mb-2 transition-colors"
        >
          <MessageCircle size={12} />
          {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          <ChevronDown size={11} className={`transition-transform ${showReplies ? 'rotate-180' : ''}`} />
        </button>
      )}

      {showReplies && replies.length > 0 && (
        <div className="pl-4 border-l border-gray-800 space-y-3 mb-3">
          {replies.map(r => (
            <div key={r.reply_id} className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-gray-800 flex items-center justify-center text-xs font-bold text-white flex-shrink-0 overflow-hidden">
                {r.user_picture
                  ? <img src={r.user_picture} alt="" className="w-full h-full object-cover" />
                  : (r.user_name || 'A')[0].toUpperCase()
                }
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-white text-xs font-semibold">{r.user_name}</span>
                  <span className="text-gray-600 text-xs">{r.created_at?.slice(0, 10)}</span>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed">{r.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {user && (
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => setShowReplyBox(v => !v)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-400 transition-colors font-medium"
            data-testid={`reply-btn-${review.review_id}`}
          >
            <MessageCircle size={12} /> {t('reply')}
          </button>
        </div>
      )}

      {showReplyBox && <ReplyBox reviewId={review.review_id} onReplied={handleReplied} />}
    </div>
  );
}

export default function ReviewsSection({ eventId }) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await reviewsAPI.list(eventId);
      setReviews(r.data.reviews || []);
      setTotal(r.data.total || 0);
      setAvgRating(r.data.avg_rating || 0);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [eventId]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 600 * 1024) { toast.error('Photo must be under 600KB'); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result.split(',')[1]);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!text.trim()) { toast.error('Please write your experience'); return; }
    setSubmitting(true);
    try {
      const r = await reviewsAPI.create({ event_id: eventId, rating, text: text.trim(), photo_base64: photo || null });
      setReviews(prev => [r.data, ...prev]);
      setTotal(t => t + 1);
      setText(''); setRating(5); setPhoto(null); setShowForm(false);
      toast.success('Review posted!');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post review');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-8" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-black uppercase text-white tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t('reviews')}
          </h2>
          <div className="mt-1">
            <AvgStars avg={avgRating} total={total} />
          </div>
        </div>
        {user && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary text-xs px-4 py-2 rounded font-bold flex items-center gap-1.5"
            data-testid="write-review-btn"
          >
            <Star size={12} fill="currentColor" /> {t('writeReview')}
          </button>
        )}
      </div>

      {/* Review form */}
      {showForm && (
        <div className="bg-[#0D0D0D] border border-gray-700 rounded-xl p-5 mb-6" data-testid="review-form">
          <h3 className="text-white font-bold text-sm mb-4">{t('yourReview')}</h3>
          <div className="mb-4">
            <p className="text-gray-500 text-xs mb-2 uppercase tracking-wide">Your Rating</p>
            <StarRating value={rating} onChange={setRating} size={24} />
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Tell others about your experience..."
            rows={4}
            className="w-full bg-[#111] border border-gray-700 text-white text-sm px-4 py-3 rounded-lg resize-none focus:border-red-500 outline-none mb-3"
            data-testid="review-text-input"
          />
          <div className="flex items-center gap-3 mb-4">
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 px-3 py-2 rounded transition-colors"
            >
              <Image size={12} /> {photo ? 'Photo added ✓' : 'Add Photo'}
            </button>
            {photo && <button onClick={() => setPhoto(null)} className="text-xs text-gray-600 hover:text-red-400 transition-colors">Remove</button>}
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400 hover:text-white transition-colors">
              {t('cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !text.trim()}
              className="flex-1 btn-primary py-2.5 rounded text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="submit-review-btn"
            >
              <Send size={14} /> {t('submitReview')}
            </button>
          </div>
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2].map(i => <div key={i} className="skeleton h-28 rounded-xl" />)}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-800 rounded-xl">
          <Star className="w-10 h-10 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t('noReviews')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map(review => (
            <ReviewCard
              key={review.review_id}
              review={review}
              currentUserId={user?.user_id}
              onDeleted={id => setReviews(prev => prev.filter(r => r.review_id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
